import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Flag, Loader2, ChevronLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORY_LABELS = {
  ai: 'AI',
  security: 'Security',
  export: 'Export',
  enterprise: 'Enterprise',
  ui: 'Interface',
  other: 'Other',
};

export default function FeatureFlagsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    (async () => {
      try {
        setFlags(await api.getFeatureFlags());
      } catch (e) {
        toast.error(e.message || 'Could not load feature flags');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (name, current) => {
    setSaving(name);
    try {
      await api.updateFeatureFlag(name, !current);
      setFlags((prev) => ({ ...prev, [name]: { ...prev[name], enabled: !current } }));
      toast.success(`${name} ${!current ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e.message || 'Could not update flag');
    } finally {
      setSaving(null);
    }
  };

  const byCategory = {};
  Object.entries(flags).forEach(([name, flag]) => {
    const cat = flag.category || 'other';
    (byCategory[cat] = byCategory[cat] || []).push({ name, ...flag });
  });

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="feature-flags-page">
      <button onClick={() => navigate('/platform-console/platform-engineering')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3" data-testid="feature-flags-back">
        <ChevronLeft className="w-4 h-4" /> Platform Console
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Flag className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold text-foreground">Feature Flags</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Toggle experimental and optional features for this workspace.
        {!isSuperAdmin && ' Changes require admin permissions.'}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center" data-testid="feature-flags-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading feature flags…
        </div>
      ) : Object.keys(byCategory).length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="feature-flags-empty">
          No feature flags are available.
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} data-testid={`feature-flags-category-${cat}`}>
              <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{CATEGORY_LABELS[cat] || cat}</h2>
              <Card className="divide-y divide-border/40">
                {items.map((f) => (
                  <div key={f.name} className="flex items-center justify-between gap-4 p-4" data-testid={`feature-flag-${f.name}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{f.description || f.name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{f.name}{f.source === 'tenant_override' ? ' · overridden' : ''}</div>
                    </div>
                    <Switch
                      checked={!!f.enabled}
                      disabled={saving === f.name}
                      onCheckedChange={() => toggle(f.name, f.enabled)}
                      data-testid={`feature-flag-toggle-${f.name}`}
                    />
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
